import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { RefreshCw } from 'lucide-react';
import useCloneProviderSecret from './CloneProvider/useCloneProviderSecret';
import SecretPrompt from './CloneProvider/SecretPrompt';
import OverviewTab from './CloneProvider/OverviewTab';
import AccountsTab from './CloneProvider/AccountsTab';
import ProvidersTab from './CloneProvider/ProvidersTab';
import ProxiesTab from './CloneProvider/ProxiesTab';
import PurchasesTab from './CloneProvider/PurchasesTab';
import ProductEvaluationsTab from './CloneProvider/ProductEvaluationsTab';
import JobsTab from './CloneProvider/JobsTab';
import ConfigTab from './CloneProvider/ConfigTab';

const TABS = [
  { key: 'overview', label: 'Overview', Component: OverviewTab },
  { key: 'accounts', label: 'Accounts', Component: AccountsTab },
  { key: 'providers', label: 'Providers', Component: ProvidersTab },
  { key: 'proxies', label: 'Proxies', Component: ProxiesTab },
  { key: 'purchases', label: 'Purchases', Component: PurchasesTab },
  { key: 'evaluations', label: 'Product Evaluations', Component: ProductEvaluationsTab },
  { key: 'jobs', label: 'Jobs', Component: JobsTab },
  { key: 'config', label: 'Config', Component: ConfigTab },
];

const DEFAULT_TAB = TABS[0].key;

export default function CloneProviderPage() {
  const { hasSecret, saveSecret, clearSecret } = useCloneProviderSecret();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabFilters, setTabFilters] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const requestedTab = searchParams.get('tab');
  const activeTab = TABS.some((tab) => tab.key === requestedTab) ? requestedTab : DEFAULT_TAB;

  if (!hasSecret) {
    return <SecretPrompt onSubmit={saveSecret} />;
  }

  function setActiveTab(tabKey) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tabKey);
      return next;
    });
  }

  function handleNavigate(tabKey, filters) {
    setTabFilters(filters || null);
    setActiveTab(tabKey);
  }

  const ActiveComponent = TABS.find((tab) => tab.key === activeTab)?.Component ?? OverviewTab;

  return (
    <section className="space-y-3">
      <Toaster position="top-right" toastOptions={{ duration: 2500 }} />

      <div className="flex items-center justify-between rounded-xl border border-cyan-100 bg-white p-2">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={[
                'whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                activeTab === tab.key
                  ? 'bg-cyan-50 text-cyan-800'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
              ].join(' ')}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setRefreshKey((k) => k + 1)}
            title="Tải lại dữ liệu"
            className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-100"
          >
            <RefreshCw size={16} />
          </button>
          <button
            type="button"
            onClick={clearSecret}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100"
          >
            Đổi secret
          </button>
        </div>
      </div>

      <ActiveComponent key={`${activeTab}-${refreshKey}`} onNavigate={handleNavigate} initialFilters={tabFilters} />
    </section>
  );
}
